import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { client, Instance, Market } from '../app/services/api/orderManagementClient';
import { UserContext } from '../components/shared/useUser';
import useLogError from '../hooks/useLogError';

interface InstanceMarketContextType {
  instances: Instance[];
  instancesLoading: boolean;
  refreshInstances: () => Promise<void>;

  /** Returns the cached markets for an instance (empty array if not loaded yet). */
  getMarkets: (instanceId: string) => Market[];
  /** True while markets for the given instance are being fetched. */
  isMarketsLoading: (instanceId: string) => boolean;
  /** Triggers a fetch of markets for the instance if not already cached/loading. */
  ensureMarketsLoaded: (instanceId: string) => void;
  /** Forces a re-fetch of markets for the instance, refreshing the cache. */
  refreshMarkets: (instanceId: string) => Promise<Market[]>;

  /** Default instance/market derived from the user's current store, if any. */
  defaultInstanceId: string;
  defaultMarketId: string;
  defaultFiltersLoading: boolean;
}

const InstanceMarketContext = createContext<InstanceMarketContextType | undefined>(undefined);

const PAGE_SIZE_ALL = 2147483647;

interface InstanceMarketProviderProps {
  children: ReactNode;
}

export const InstanceMarketProvider: React.FC<InstanceMarketProviderProps> = ({ children }) => {
  const { logError } = useLogError();
  const { selectedStore } = useContext(UserContext);

  const [instances, setInstances] = useState<Instance[]>([]);
  const [instancesLoading, setInstancesLoading] = useState<boolean>(false);
  const instancesLoadedRef = useRef<boolean>(false);
  const instancesLoadingPromiseRef = useRef<Promise<void> | null>(null);

  const [marketsByInstance, setMarketsByInstance] = useState<Record<string, Market[]>>({});
  const [marketsLoadingByInstance, setMarketsLoadingByInstance] = useState<Record<string, boolean>>({});
  const marketsLoadingPromisesRef = useRef<Record<string, Promise<Market[]> | undefined>>({});

  const [defaultInstanceId, setDefaultInstanceId] = useState<string>('');
  const [defaultMarketId, setDefaultMarketId] = useState<string>('');
  const [defaultFiltersLoading, setDefaultFiltersLoading] = useState<boolean>(false);
  const defaultFiltersComputedForStoreRef = useRef<string | null>(null);

  const fetchInstances = useCallback(async (): Promise<void> => {
    setInstancesLoading(true);
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: PAGE_SIZE_ALL,
      });

      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
      instancesLoadedRef.current = true;
    } catch (error) {
      logError('Failed to load instances');
    } finally {
      setInstancesLoading(false);
    }
  }, [logError]);

  const refreshInstances = useCallback(async (): Promise<void> => {
    instancesLoadingPromiseRef.current = fetchInstances();
    await instancesLoadingPromiseRef.current;
    instancesLoadingPromiseRef.current = null;
  }, [fetchInstances]);

  // Load instances once, centrally, on first mount of the provider.
  useEffect(() => {
    if (!instancesLoadedRef.current && !instancesLoadingPromiseRef.current) {
      instancesLoadingPromiseRef.current = fetchInstances();
    }
  }, [fetchInstances]);

  const fetchMarkets = useCallback(async (instanceId: string): Promise<Market[]> => {
    setMarketsLoadingByInstance(prev => ({ ...prev, [instanceId]: true }));
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: PAGE_SIZE_ALL,
      });

      const items = response.success && response.data ? response.data.items || [] : [];
      setMarketsByInstance(prev => ({ ...prev, [instanceId]: items }));
      return items;
    } catch (error) {
      logError('Failed to load markets');
      return [];
    } finally {
      setMarketsLoadingByInstance(prev => ({ ...prev, [instanceId]: false }));
      delete marketsLoadingPromisesRef.current[instanceId];
    }
  }, [logError]);

  const refreshMarkets = useCallback((instanceId: string): Promise<Market[]> => {
    if (!instanceId) return Promise.resolve([]);
    const promise = fetchMarkets(instanceId);
    marketsLoadingPromisesRef.current[instanceId] = promise;
    return promise;
  }, [fetchMarkets]);

  const ensureMarketsLoaded = useCallback((instanceId: string): void => {
    if (!instanceId) return;
    if (marketsByInstance[instanceId] || marketsLoadingPromisesRef.current[instanceId]) return;
    marketsLoadingPromisesRef.current[instanceId] = fetchMarkets(instanceId);
  }, [fetchMarkets, marketsByInstance]);

  const getMarkets = useCallback((instanceId: string): Market[] => {
    if (!instanceId) return [];
    return marketsByInstance[instanceId] || [];
  }, [marketsByInstance]);

  const isMarketsLoading = useCallback((instanceId: string): boolean => {
    if (!instanceId) return false;
    return !!marketsLoadingByInstance[instanceId];
  }, [marketsLoadingByInstance]);

  // Compute the default instance/market for the current store once, and cache the result.
  useEffect(() => {
    const storeKey = selectedStore?.storeNumber && selectedStore?.franchiseUUID
      ? `${selectedStore.storeNumber}|${selectedStore.franchiseUUID}`
      : null;

    if (!storeKey || defaultFiltersComputedForStoreRef.current === storeKey) return;

    let cancelled = false;
    const computeDefaults = async () => {
      setDefaultFiltersLoading(true);
      try {
        const companyResult = await client.getCompanyIdByStoreNumber(selectedStore!.storeNumber!);
        if (!companyResult.success || !companyResult.data || cancelled) return;

        const instanceId = companyResult.data.toString();
        const markets = marketsByInstance[instanceId] || (await refreshMarkets(instanceId));
        if (cancelled) return;

        const storeMarket = markets.find(m => m.uuid === selectedStore!.franchiseUUID);
        if (storeMarket) {
          setDefaultInstanceId(storeMarket.companyId.toString());
          setDefaultMarketId(storeMarket.customerTypeId.toString());
        }
        defaultFiltersComputedForStoreRef.current = storeKey;
      } catch (error) {
        logError('Failed to load store default filters');
      } finally {
        if (!cancelled) setDefaultFiltersLoading(false);
      }
    };

    computeDefaults();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.storeNumber, selectedStore?.franchiseUUID]);

  const value: InstanceMarketContextType = useMemo(() => ({
    instances,
    instancesLoading,
    refreshInstances,
    getMarkets,
    isMarketsLoading,
    ensureMarketsLoaded,
    refreshMarkets,
    defaultInstanceId,
    defaultMarketId,
    defaultFiltersLoading,
  }), [
    instances,
    instancesLoading,
    refreshInstances,
    getMarkets,
    isMarketsLoading,
    ensureMarketsLoaded,
    refreshMarkets,
    defaultInstanceId,
    defaultMarketId,
    defaultFiltersLoading,
  ]);

  return (
    <InstanceMarketContext.Provider value={value}>
      {children}
    </InstanceMarketContext.Provider>
  );
};

export const useInstanceMarket = (): InstanceMarketContextType => {
  const context = useContext(InstanceMarketContext);
  if (context === undefined) {
    throw new Error('useInstanceMarket must be used within an InstanceMarketProvider');
  }
  return context;
};
